import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import CommonHeadingNBtns from "../common-components/CommonHeadingNBtns";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";
import { add_team_member, update_team_member } from "../../data/ApiEndpoints";
import { teamMemberSchema } from "../../schema/website-schema";
import "../../styles/website/management-forms.css";

const ManageTeamMember = ({
  closeModal,
  token,
  initialValues,
  isEdit,
  getTeamData,
  departments = [],
}) => {
  const dispatch = useDispatch();
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();
  const [isLoading, setIsLoading] = useState(false);

  // Process initial values
  const processedInitialValues = () => {
    if (!initialValues) {
      return {
        name: "",
        department_uid: "",
        email: "",
        bio: "",
        profile: "",
        seq_no: "",
      };
    }

    // Return processed initial values
    const processedValues = {
      ...initialValues,
      // Ensure numeric fields are properly typed
      seq_no: initialValues.seq_no || "",
      // Map type to department_uid if needed for backward compatibility
      department_uid: initialValues.department_uid || "",
    };

    console.log("Processed initial values:", processedValues);
    return processedValues;
  };

  const formik = useFormik({
    initialValues: processedInitialValues(),
    validationSchema: teamMemberSchema,
    onSubmit: async (values) => {
      await submitToApi(values);
    },
  });

  // Independent API submission function
  const submitToApi = async (values) => {
    setIsLoading(true);

    try {
      // Determine method and endpoint
      const method = isEdit ? "PUT" : "POST";
      let endpoint;

      if (isEdit && initialValues?.uid) {
        endpoint = `${update_team_member}/${initialValues.uid}`;
      } else {
        endpoint = `${add_team_member}`;
      }

      // Prepare payload
      const payload = { ...values };

      // Add UID for edit operations
      if (isEdit && initialValues?.uid) {
        payload.uid = initialValues.uid;
        console.log("Added UID to payload:", payload.uid);
      }

      console.log("Final API payload:", payload);

      // Remove empty values
      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === undefined ||
          payload[key] === null ||
          payload[key] === ""
        ) {
          delete payload[key];
        }
      });

      // Make API call
      console.log("Making API call directly:", {
        method,
        endpoint,
        payload,
        hasToken: !!token,
      });

      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      console.log("API response:", result);

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Success!");
        formik.resetForm();
        getTeamData && getTeamData();
        closeModal();
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("API submission error:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  // Create a direct submission handler
  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    // Touch all fields to show all validation errors immediately
    const touchedFields = {};
    Object.keys(formik.values).forEach((key) => {
      touchedFields[key] = true;
    });
    formik.setTouched(touchedFields);

    // Validate form before submission
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      await submitToApi(formik.values);
    } else {
      // Create error message listing all validation issues
      const errorMessages = Object.entries(errors).map(([field, message]) => {
        const fieldName =
          field.charAt(0).toUpperCase() + field.slice(1).replace("_", " ");
        return `${fieldName}: ${message}`;
      });

      // Show toast with the first error
      toast.error(`Please fix these errors: ${errorMessages[0]}`);

      // Scroll to the first error field
      const firstErrorField = document.getElementById(Object.keys(errors)[0]);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        firstErrorField.focus();
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setIsLoading(true);
        console.log("Uploading image to Cloudfront...");
        const imageUrl = await uploadToCloudfront(file, token);
        if (imageUrl) {
          console.log("Image uploaded successfully:", imageUrl);
          formik.setFieldValue("profile", imageUrl);
        } else {
          toast.error("Failed to upload profile image");
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Image upload failed");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="createNewCTOModalContainer mod-width-50p">
      <CommonHeadingNBtns
        title={isEdit ? "Update Team Member" : "Add New Member"}
        onClose={closeModal}
        btnTxt={
          isLoading || isUploading ? "Processing..." : isEdit ? "Update" : "Add"
        }
        onClick={handleFormSubmit}
        isDisabled={isLoading || isUploading}
      />

      <form className="management-form-container" onSubmit={handleFormSubmit}>
        <div className="form-grid form-grid-2">
          {/* Name */}
          <div className="management-form-group">
            <label
              htmlFor="name"
              className={`management-form-label ${
                formik.touched.name && formik.errors.name ? "error" : ""
              }`}
            >
              Name <span className="required-indicator">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter name"
              className={`management-form-input ${
                formik.touched.name && formik.errors.name ? "error" : ""
              }`}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="management-form-error">{formik.errors.name}</div>
            )}
          </div>

          {/* Email */}
          <div className="management-form-group">
            <label
              htmlFor="email"
              className={`management-form-label ${
                formik.touched.email && formik.errors.email ? "error" : ""
              }`}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter email address"
              className={`management-form-input management-email-input ${
                formik.touched.email && formik.errors.email ? "error" : ""
              }`}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="management-form-error">{formik.errors.email}</div>
            )}
          </div>

          {/* Department Type */}
          <div className="management-form-group">
            <label
              htmlFor="department_uid"
              className={`management-form-label ${
                formik.touched.department_uid && formik.errors.department_uid
                  ? "error"
                  : ""
              }`}
            >
              Department <span className="required-indicator">*</span>
            </label>
            <select
              id="department_uid"
              name="department_uid"
              value={formik.values.department_uid}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`management-form-input ${
                formik.touched.department_uid && formik.errors.department_uid
                  ? "error"
                  : ""
              }`}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.uid} value={dept.uid}>
                  {dept.title}
                </option>
              ))}
            </select>
            {formik.touched.department_uid && formik.errors.department_uid && (
              <div className="management-form-error">
                {formik.errors.department_uid}
              </div>
            )}
          </div>

          {/* Designation (formerly Bio) */}
          <div className="management-form-group">
            <label
              htmlFor="bio"
              className={`management-form-label ${
                formik.touched.bio && formik.errors.bio ? "error" : ""
              }`}
            >
              Designation
            </label>
            <input
              id="bio"
              type="text"
              name="bio"
              value={formik.values.bio}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter designation"
              className={`management-form-input ${
                formik.touched.bio && formik.errors.bio ? "error" : ""
              }`}
            />
            {formik.touched.bio && formik.errors.bio && (
              <div className="management-form-error">{formik.errors.bio}</div>
            )}
          </div>

          {/* Sequence Number */}
          <div className="management-form-group">
            <label
              htmlFor="seq_no"
              className={`management-form-label ${
                formik.touched.seq_no && formik.errors.seq_no ? "error" : ""
              }`}
            >
              Sequence Number <span className="required-indicator">*</span>
            </label>
            <input
              id="seq_no"
              type="number"
              name="seq_no"
              value={formik.values.seq_no}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter sequence number"
              className={`management-form-input ${
                formik.touched.seq_no && formik.errors.seq_no ? "error" : ""
              }`}
            />
            {formik.touched.seq_no && formik.errors.seq_no && (
              <div className="management-form-error">
                {formik.errors.seq_no}
              </div>
            )}
          </div>

          <br />

          {/* Profile Image */}
          <div className="management-form-group">
            <label
              htmlFor="profile"
              className={`management-form-label ${
                formik.touched.profile && formik.errors.profile ? "error" : ""
              }`}
            >
              Profile Image <span className="required-indicator">*</span>
            </label>
            <div className="management-image-upload">
              {formik.values.profile && (
                <div className="management-image-preview">
                  <img src={formik.values.profile} alt="Profile Preview" />
                </div>
              )}
              <div className="management-image-upload-inputs">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ marginBottom: "5px" }}
                />
                <input
                  id="profile"
                  type="text"
                  name="profile"
                  value={formik.values.profile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Or enter image URL"
                  className={`management-form-input ${
                    formik.touched.profile && formik.errors.profile
                      ? "error"
                      : ""
                  }`}
                />
              </div>
            </div>
            {formik.touched.profile && formik.errors.profile && (
              <div className="management-form-error">
                {formik.errors.profile}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ManageTeamMember;
